import { useQueryClient } from '@tanstack/react-query'
import { Button, DatePicker, Flex, Form, Modal, ModalProps, Select } from 'antd'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'

import {
  usePreScreeningServiceGetRiskGroupPersonEndpointKey,
  usePreScreeningServiceUpdateRiskGroupPersonEndpoint
} from 'src/api/queries'
import { PersonScreeningStatus } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { SaveConfirm } from 'src/components'
import { getPersonRiskGroupSelectOptions } from 'src/features/riskGroups/utils'
import { dayMonthYearBackSlash, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'

type Props = {
  personId?: string
  onToggle: () => void
} & ModalProps

export const ChangeStatusModal = ({ personId, onToggle, ...props }: Props) => {
  const { t } = useLocales()

  const { riskId } = useParams()
  const [form] = Form.useForm()

  const queryClient = useQueryClient()

  const { mutate } = usePreScreeningServiceUpdateRiskGroupPersonEndpoint({
    onSuccess: () => {
      onToggle()
      queryClient.invalidateQueries({
        queryKey: [
          usePreScreeningServiceGetRiskGroupPersonEndpointKey,
          { riskGroupId: riskId, personId }
        ]
      })
    }
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    mutate({
      personId: String(personId),
      riskGroupId: String(riskId),
      requestBody: {
        screeningStatus: values.status,
        screeningDate: values.date
          ? dayjs(values.date).format(yearMonthDaySlash)
          : undefined
      }
    })
  }

  const status = Form.useWatch('status', form)

  const options = getPersonRiskGroupSelectOptions(t)

  return (
    <Modal
      open
      centered
      maskClosable={false}
      onCancel={onToggle}
      title={t('form.status')}
      footer={
        <Flex justify="center" gap={8}>
          <Button onClick={onToggle}>{t('common.close')}</Button>

          <SaveConfirm title={`${t('common.save')}?`} onConfirm={onSubmit}>
            <Button type="primary">{t('common.save')}</Button>
          </SaveConfirm>
        </Flex>
      }
      {...props}
    >
      <Form layout="vertical" requiredMark={false} form={form}>
        <Flex vertical gap={24}>
          <Form.Item
            name="status"
            label={t('form.status')}
            rules={requiredRules}
          >
            <Select options={options} />
          </Form.Item>

          {status === PersonScreeningStatus.PRE_SCREENING && (
            <Form.Item
              name="date"
              label={t('common.date')}
              rules={requiredRules}
            >
              <DatePicker
                className="full-width"
                placeholder={t('common.date')}
                format={dayMonthYearBackSlash}
              />
            </Form.Item>
          )}
        </Flex>
      </Form>
    </Modal>
  )
}
